import {
  CardNumberElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import { type PropsWithChildren, createContext, useContext } from 'react';

import { handleSaveCardOnFileMutation } from 'payments/helpers/handleSaveCardOnFileMutation';
import { getStripeErrorSnackbarArgs } from 'payments/helpers/stripe';
import type {
  PartialSaveOptions,
  PaymentFormContextInterface,
  SaveCardOnFileMutation
} from 'payments/types/form';
import { useErrorSnackbar } from 'reservations/base/hooks/useSnackbar';
import { useLoadingSpinner } from 'reservations/base/providers/LoadingSpinnerProvider';

const StripePaymentFormContext =
  createContext<PaymentFormContextInterface | null>(null);

export const StripePaymentFormProvider = ({ children }: PropsWithChildren) => {
  const { handleHasLoaded } = useLoadingSpinner();
  const enqueueErrorSnackbar = useErrorSnackbar();

  // NOTE: This is the useStripe from the library
  const stripe = useStripe();
  const elements = useElements();

  const saveCardOnFile = async (
    saveOptions: PartialSaveOptions,
    mutate: SaveCardOnFileMutation
  ) => {
    const cardElement = elements?.getElement(CardNumberElement);

    if (!cardElement) {
      throw new Error('Card element is undefined');
    }

    const tokenResult = await stripe?.createToken(cardElement);

    if (!tokenResult) {
      throw new Error(
        'Something went wrong when consuming the Stripe CreateToken API'
      );
    }

    const { token, error } = tokenResult;

    if (error) {
      const errorCode = error.code || '';
      const stripeErrors = getStripeErrorSnackbarArgs(errorCode);
      const { key, message } = stripeErrors;
      enqueueErrorSnackbar(key, message);
      handleHasLoaded();
      return;
    }

    if (!token) {
      throw new Error('Token generated from Stripe is undefined');
    }

    handleSaveCardOnFileMutation(saveOptions, mutate, token.id);
    return;
  };

  return (
    <StripePaymentFormContext.Provider value={{ saveCardOnFile }}>
      {children}
    </StripePaymentFormContext.Provider>
  );
};

export const useStripePaymentForm = () => useContext(StripePaymentFormContext);
